import type { City, StopActions, TripLeg } from "../data/types";
import { getCityById, initCityStore } from "./cityStore";
import { parseTripSnapshot } from "./storageService";
import { apiFetch } from "./api";
import { extractCityName } from "./geoUtils";

function compactCity(city: City) {
  const { id, name, country, population, latitude, longitude, hafasId } = city;
  return { id, name, city: city.city, country, population, latitude, longitude, hafasId };
}

/** A guest link carries the cities itself, including newly discovered stations. */
export function encodeTripToUrl(stops: City[], tripName: string, travelDate: string, stopActions: StopActions, legs: TripLeg[] = []): string {
  const snapshot = parseTripSnapshot({ stops, tripName, travelDate, stopActions, legs });
  const compact = { ...snapshot, stops: snapshot.stops.map(compactCity), legs: snapshot.legs.map(leg => ({
    ...leg, stationData: leg.stationData?.map(s => ({ ...compactCity(s), arrival: s.arrival, departure: s.departure,
      arrivalInstant: s.arrivalInstant, departureInstant: s.departureInstant })),
  })) };
  const url = new URL(globalThis.location.pathname, globalThis.location.origin);
  url.searchParams.set("tripv2", JSON.stringify(compact));
  if (url.href.length > 8000) throw new Error("This trip is too large for a guest link. Please sign in to share it.");
  return url.href;
}

export async function decodeTripFromUrl(input = globalThis.location.href) {
  const params = new URL(input).searchParams;
  const current = params.get("tripv2");
  if (current) {
    if (current.length > 64000) throw new Error("Shared trip is too large");
    return parseTripSnapshot(JSON.parse(current));
  }
  const legacy = params.get("trip");
  if (!legacy) return null;
  await initCityStore();
  const ids = legacy.split(",").filter(Boolean);
  if (!ids.length || ids.length > 100) throw new Error("Invalid shared trip");
  const stops = await Promise.all(ids.map(async (id) => {
    const cached = getCityById(id);
    if (cached) return cached;
    if (!/^\d{5,12}$/.test(id)) throw new Error("A shared station could not be found");
    const stop = await apiFetch<{ id: string; name: string; location: { latitude: number; longitude: number } }>(`/stops/${id}`);
    return { id: stop.id, name: stop.name, city: extractCityName(stop.name), country: "", population: 0, ...stop.location };
  }));
  const stopActions: StopActions = {};
  for (const part of (params.get("actions") || "").split(",")) {
    const [id, flags] = part.split(":");
    if (!flags || !ids.includes(id) || ["__proto__", "constructor", "prototype"].includes(id)) continue;
    stopActions[id] = { food: flags.includes("f"), overnight: flags.includes("o"), fika: flags.includes("k") };
  }
  return parseTripSnapshot({ stops, stopActions, tripName: params.get("name") || "Shared Trip",
    travelDate: params.get("date") || new Date().toISOString().slice(0, 10), legs: [] });
}
